/**
 * Auth session coordinator (ARCHITECTURE.md R23, R26, R3).
 *
 * Prevents login loops caused by:
 * - 401 from non-auth endpoints during cold-start bootstrap clearing JWT + hard redirect
 * - authStatus stuck on 'loading' while PrivateRoute ↔ /login fight over token presence
 * - duplicate redirect logic in LoginForm, App.js, and api.js interceptors
 */

/** Match bootstrapApi / login cold-start window (R23). */
export const AUTH_BOOTSTRAP_GRACE_MS = 120_000;

export const FORCE_LOGOUT_EVENT = 'archon:force-logout';

let bootstrapUntilMs = 0;

export function beginAuthBootstrap() {
  bootstrapUntilMs = Date.now() + AUTH_BOOTSTRAP_GRACE_MS;
}

export function endAuthBootstrap() {
  bootstrapUntilMs = 0;
}

/** True while post-login / page-reload session is still warming (suppress 401 hard logout). */
export function isAuthBootstrapActive() {
  return bootstrapUntilMs > 0 && Date.now() < bootstrapUntilMs;
}

/**
 * During bootstrap, only AuthContext `/users/me` may revoke the JWT on 401.
 * Other 401s are ignored to avoid login redirect loops under pool contention.
 */
export function shouldSuppress401Logout() {
  return isAuthBootstrapActive();
}

export function dispatchForceLogout(detail = {}) {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(FORCE_LOGOUT_EVENT, { detail }));
}

export function subscribeForceLogout(handler) {
  if (typeof window === 'undefined') return () => {};
  window.addEventListener(FORCE_LOGOUT_EVENT, handler);
  return () => window.removeEventListener(FORCE_LOGOUT_EVENT, handler);
}

/** Decode a standard JWT `exp` (ms epoch) or null if absent/unparseable (e.g. offline token). */
export function getTokenExpiryMs(token) {
  if (!token || typeof token !== 'string') return null;
  const parts = token.split('.');
  if (parts.length !== 3) return null;
  try {
    const normalized = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const payload = JSON.parse(atob(normalized));
    return typeof payload?.exp === 'number' ? payload.exp * 1000 : null;
  } catch {
    return null;
  }
}

/**
 * True only when we can read `exp` AND it is in the past (with skew).
 * Unparseable / offline tokens return false so they are never falsely revoked here.
 */
export function isJwtExpired(token, skewMs = 10_000) {
  const expMs = getTokenExpiryMs(token);
  if (expMs == null) return false;
  return Date.now() >= expMs - skewMs;
}

/**
 * Single source of truth for "is there a usable session?" — a token that is present
 * and not provably expired. Both PrivateRoute and usePostLoginRedirect MUST use this
 * same predicate so they can never disagree and ping-pong /login ↔ /app.
 */
export function hasUsableSession(token) {
  return Boolean(token) && !isJwtExpired(token);
}
